import React from 'react';

import AppendToBody from 'sharedreact/components/AppendToBody';
import Button from 'sharedreact/components/Button';
import Tab from 'sharedreact/components/Tab';
import Toolbar from 'sharedreact/components/Toolbar';
import ToolbarGroup from 'sharedreact/components/ToolbarGroup';
import ToolbarTitle from 'sharedreact/components/ToolbarTitle';
import { translate } from 'sharedreact/utils/LanguageSupport';

import EskoIDSignInDialog from 'workspaces/launcher/components/admin/EskoIDSignInDialog';
import UsageOptOutContainer from 'workspaces/launcher/containers/admin/UsageOptOutContainer';

type Props = {
  index: number,
  activeTab: number,
  language: string,
  didOptIn: boolean,
  userName: string,
  isForTestEnv: boolean,

  onLogin: (userName: string, password: string) => void,
  onLoad: () => void
};

type State = {
  isExtraInfoVisible: boolean,
  isSignInVisible: boolean,
  isConfirmOptOutVisible: boolean
};

class UsageTrackingTab extends React.Component<Props, State> {
  constructor (props: Props) {
    super(props);

    this.state = {
      isExtraInfoVisible: false,
      isSignInVisible: false,
      isConfirmOptOutVisible: false
    };

    this.toggleExtraInfo = this.toggleExtraInfo.bind(this);
    this.showSignInDialog = this.showSignInDialog.bind(this);
    this.hideSignInDialog = this.hideSignInDialog.bind(this);
    this.showConfirmOptOutDialog = this.showConfirmOptOutDialog.bind(this);
    this.hideConfirmOptOutDialog = this.hideConfirmOptOutDialog.bind(this);
  }

  override componentDidMount (): void {
    if (this.props.index === this.props.activeTab) {
      this.props.onLoad();
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props>): void {
    if ((this.props.activeTab !== prevProps.activeTab || this.props.index !== prevProps.index) &&
      this.props.index === this.props.activeTab) {
      this.props.onLoad();
    }
  }

  toggleExtraInfo () {
    this.setState({isExtraInfoVisible: !this.state.isExtraInfoVisible});
  }

  showSignInDialog () {
    this.setState({isSignInVisible: true});
  }

  hideSignInDialog () {
    this.setState({isSignInVisible: false});
  }

  showConfirmOptOutDialog () {
    this.setState({isConfirmOptOutVisible: true});
  }

  hideConfirmOptOutDialog () {
    this.setState({isConfirmOptOutVisible: false});
  }

  getLegalUrl (language: string): string {
    let languageCodeForUrl: string = 'en';
    if (language === 'de' ||
      language === 'fr' ||
      language === 'it' ||
      language === 'es' ||
      language === 'ko') {
      languageCodeForUrl = language;
    } // 'ja', 'zh', 'zh-TW' are not supported, so the english page is used instead

    return 'https://www.esko.com/' + languageCodeForUrl + '/legal/privacy-policy';
  }

  renderMoreInfo () {
    const { language } = this.props;

    const isExtraInfoVisible: boolean = this.state.isExtraInfoVisible;

    let moreInfoComponents: any[] = [];

    moreInfoComponents.push(
      <div key='moreinfobutton'>
        <Button id='ae_button_usage_moreinfo'
          className='webappslauncher-usage-link-moreinfo'
          type='btn-link'
          onClick={this.toggleExtraInfo}>
          {isExtraInfoVisible ? translate('launcher.usage.link.lessinfo') : translate('launcher.usage.link.moreinfo')}
        </Button>
      </div>
    );

    if (isExtraInfoVisible) {
      moreInfoComponents.push(
        <div key='moreinfo' className='webappslauncher-usage-optin-moreinfolayout'>
          <h3>{translate('launcher.usage.moreinfo.title1')}</h3>
          <p>{translate('launcher.usage.moreinfo.part1')}</p>
          <h3>{translate('launcher.usage.moreinfo.title5')}</h3>
          <span>{translate('launcher.usage.moreinfo.part5')}</span><br />
          <ul>
            <li>{translate('launcher.usage.moreinfo.part6')}</li>
            <li>{translate('launcher.usage.moreinfo.part7')}</li>
            <li>{translate('launcher.usage.moreinfo.part8')}</li>
          </ul>
          <span>{translate('launcher.usage.moreinfo.part9')}</span><br />
          <span>{translate('launcher.usage.moreinfo.part10')}</span>
          <h3>{translate('launcher.usage.moreinfo.title2')}</h3>
          <p>{translate('launcher.usage.moreinfo.part2')}</p>
          <a id='ae_link_usage_privacy' href={this.getLegalUrl(language)} target='_blank'>{translate('launcher.usage.moreinfo.privacypolicy')}</a><br />
          <h3>{translate('launcher.usage.moreinfo.title3')}</h3>
          <p>{translate('launcher.usage.moreinfo.part3')}</p>
          <h3>{translate('launcher.usage.moreinfo.title4')}</h3>
          <p>{translate('launcher.usage.moreinfo.part4')}</p>
        </div>
      );
    }

    return moreInfoComponents;
  }

  renderContent () {
    const { didOptIn, userName, isForTestEnv } = this.props;

    const dashboardLink: string = isForTestEnv ? 'https://usage.next.dev.cloudi.city' : 'https://usage.eu.esko.cloud';

    if (didOptIn) {
      return (
        <div className='webappslauncher-stretch-container'>
          <span>{translate('launcher.usage.info.collecting', userName)}</span><br />
          <a id='ae-usage-goto-dashboard' href={dashboardLink} target='_blank' rel='noreferrer noopener'>{translate('launcher.usage.link.gotodashboard')}</a>
          {this.renderMoreInfo()}
          <div className='webappslauncher-usage-buttonlayout'>
            <Button id='ae_button_usage_optout'
              onClick={this.showConfirmOptOutDialog}>
              {translate('launcher.usage.button.optout')}
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className='webappslauncher-stretch-container'>
          <h1>{translate('launcher.usage.label.use')}</h1>
          <span>{translate('launcher.usage.info')}</span>
          <ul>
            <li>{translate('launcher.usage.info1')}</li>
            <li>{translate('launcher.usage.info2')}</li>
            <li>{translate('launcher.usage.info3')}</li>
          </ul>
          <span>{translate('launcher.usage.collect')}</span>
          {this.renderMoreInfo()}
          <div className='webappslauncher-usage-buttonlayout'>
            <Button id='ae_button_usage_optin'
              type='btn-primary'
              onClick={this.showSignInDialog}>
              {translate('launcher.usage.button.optin')}
            </Button>
          </div>
        </div>
      );
    }
  }

  override render (): React.ReactNode {
    const { index, activeTab, language,
      onLogin, isForTestEnv
    } = this.props;

    const { isSignInVisible, isConfirmOptOutVisible } = this.state;

    return (
      <Tab index={index} activeIndex={activeTab}>
        <Toolbar>
          <ToolbarGroup>
            <ToolbarTitle title={translate('launcher.usage.title')} />
          </ToolbarGroup>
        </Toolbar>
        <AppendToBody>
          <EskoIDSignInDialog
            language={language}
            show={isSignInVisible}
            onLogin={onLogin}
            onClose={this.hideSignInDialog}
            isForTestEnv={isForTestEnv} />
          <UsageOptOutContainer show={isConfirmOptOutVisible} onClose={this.hideConfirmOptOutDialog} allowRemoveData={false} />
        </AppendToBody>
        <div className='webappslauncher-scroll-container'>
          {this.renderContent()}
        </div>
      </Tab>
    );
  }
}

export default UsageTrackingTab;
